import React from 'react';
import './about.scss';
import { 
  FiChevronRight, 
  FiCheck, 
  FiMapPin, 
  FiShield, 
  FiUsers, 
  FiGlobe, 
  FiAward, 
  FiClock,
  FiStar,
  FiTarget,
  FiEye
} from "react-icons/fi";
import { 
  FaArrowRight, 
  FaFacebook, 
  FaTwitter, 
  FaLinkedin, 
  FaInstagram,
  FaMountain,
  FaPassport,
  FaHandshake,
  FaHeadset,
  FaCertificate,
  FaLeaf
} from "react-icons/fa";
import { Helmet } from "react-helmet";
import MaroofAwaisQarni from '../Assets/About/MaroofAwaisQarni.jpeg';
import MuhammadSiddiq from '../Assets/About/MuhammadSiddiq.jpg';
import Francescs from '../Assets/About/Francesca.jpeg';
import Joy from '../Assets/About/Joy.jpeg';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us | World Adventures Tours</title>
        <meta name="description" content="Learn about World Adventures Tours — a registered tour company in Gilgit-Baltistan offering adventure travel, mountaineering support, cultural tours, and more across Pakistan." />
        <meta name="keywords" content="About World Adventures Tours, Pakistan travel agency, Gilgit Baltistan tours, adventure travel Pakistan, trekking company Pakistan" />
        <meta property="og:title" content="About Us | World Adventures Tours" />
        <meta property="og:description" content="World Adventures Tours is a government-licensed company offering authentic adventure and cultural tours across Pakistan's northern regions. Explore our values and services." />
        <meta property="og:url" content="https://www.worldadventurestours.com/About" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.worldadventurestours.com/About" />
      </Helmet>

      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-content">
          <div className="hero-text">
            <span className="hero-badge">Government Registered • License No: DTS-GB-021</span>
            <h1>World Adventures Tours</h1>
            <p className="hero-subtitle">
              Your Gateway to Pakistan's Majestic Northern Landscapes
            </p>
            <p className="hero-description">
              A registered and licensed tour operator based in the heart of Gilgit-Baltistan, 
              connecting global explorers with the wonders of Pakistan's mountains, culture, and adventure.
            </p>
            <div className="hero-stats">
              <div className="stat">
                <FiAward />
                <div>
                  <span className="stat-number">10+</span>
                  <span className="stat-label">Years Experience</span>
                </div>
              </div>
              <div className="stat">
                <FiUsers />
                <div>
                  <span className="stat-number">5000+</span>
                  <span className="stat-label">Happy Travelers</span>
                </div>
              </div>
              <div className="stat">
                <FiMapPin />
                <div>
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Destinations</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="about-container">
        {/* Who We Are Section */}
        <section className="about-section who-we-are">
          <div className="section-header">
            <span className="section-label">About Us</span>
            <h2>Who Are We?</h2>
            <p className="section-intro">
              World Adventures Tours is more than just a travel company — we're a bridge between cultures, 
              nature, and people.
            </p>
          </div>
          
          <div className="content-grid">
            <div className="content-text">
              <p>
                Officially registered with the Government of Gilgit-Baltistan under license number{' '}
                <strong className="highlight">DTS-GB-021</strong>, we are fully authorized to operate 
                tourism services across the region. We're a passionate team dedicated to sharing the 
                breathtaking beauty of Pakistan with the world.
              </p>
              <p>
                Based in Gilgit-Baltistan, we specialize in creating unforgettable adventure experiences 
                while promoting sustainable tourism and cultural exchange. Our core values are built on{' '}
                <strong>trust, commitment, and flexibility</strong>, ensuring every journey with us is 
                safe, authentic, and memorable.
              </p>
              <div className="values-grid">
                <div className="value-item">
                  <div className="value-icon">
                    <FaHandshake />
                  </div>
                  <h4>Trust & Integrity</h4>
                  <p>Building lasting relationships through transparency and reliability</p>
                </div>
                <div className="value-item">
                  <div className="value-icon">
                    <FiTarget />
                  </div>
                  <h4>Commitment</h4>
                  <p>Dedicated to delivering exceptional experiences beyond expectations</p>
                </div>
                <div className="value-item">
                  <div className="value-icon">
                    <FaLeaf />
                  </div>
                  <h4>Sustainability</h4>
                  <p>Promoting responsible tourism that protects nature and supports communities</p>
                </div>
              </div>
            </div>
            
            <div className="content-visual">
              <div className="license-card">
                <div className="license-header">
                  <FaCertificate />
                  <h3>Official License</h3>
                </div>
                <div className="license-body">
                  <div className="license-number">
                    <span className="label">License No:</span>
                    <span className="number">DTS-GB-021</span>
                  </div>
                  <div className="license-details">
                    <p>
                      <FiCheck /> Registered with Government of Gilgit-Baltistan
                    </p>
                    <p>
                      <FiCheck /> Authorized Tourism Services Provider
                    </p>
                    <p>
                      <FiCheck /> Fully Insured & Bonded
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Services */}
        <section className="about-section services-section">
          <div className="section-header">
            <span className="section-label">What We Offer</span>
            <h2>Our Services</h2>
            <p className="section-intro">
              Comprehensive travel solutions for every type of adventurer
            </p>
          </div>

          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <FaMountain />
              </div>
              <h3>Adventure Tours</h3>
              <p>Guided treks to K2 Base Camp, Concordia, and other iconic mountain destinations</p>
              <ul className="service-features">
                <li><FiChevronRight /> Expert mountain guides</li>
                <li><FiChevronRight /> All-inclusive packages</li>
                <li><FiChevronRight /> Safety equipment provided</li>
              </ul>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <FiShield />
              </div>
              <h3>Mountaineering Support</h3>
              <p>Complete base camp management, porter services, and expedition logistics</p>
              <ul className="service-features">
                <li><FiChevronRight /> Base camp setup</li>
                <li><FiChevronRight /> Porter & guide services</li>
                <li><FiChevronRight /> Equipment rental</li>
              </ul>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <FiGlobe />
              </div>
              <h3>Cultural Tours</h3>
              <p>Immerse in the rich heritage, architecture, and traditions of Northern Pakistan</p>
              <ul className="service-features">
                <li><FiChevronRight /> Local community experiences</li>
                <li><FiChevronRight /> Heritage site visits</li>
                <li><FiChevronRight /> Cultural workshops</li>
              </ul>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <FaPassport />
              </div>
              <h3>Travel Logistics</h3>
              <p>End-to-end travel support from permits to accommodation</p>
              <ul className="service-features">
                <li><FiChevronRight /> Permit assistance</li>
                <li><FiChevronRight /> Hotel bookings</li>
                <li><FiChevronRight /> Transportation</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="about-section why-choose-us">
          <div className="section-header">
            <span className="section-label">Why Choose Us</span>
            <h2>Your Trusted Adventure Partner</h2>
            <p className="section-intro">
              10 reasons why thousands of travelers choose World Adventures Tours
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">
                <FaCertificate />
              </div>
              <div className="feature-content">
                <h3>Government Registered</h3>
                <p>Fully licensed and authorized by the Department of Tourism Gilgit-Baltistan (License: DTS-GB-021)</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <FiClock />
              </div>
              <div className="feature-content">
                <h3>10+ Years Experience</h3>
                <p>Over a decade of expertise in adventure travel and mountain expeditions</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <FiMapPin />
              </div>
              <div className="feature-content">
                <h3>Local Expertise</h3>
                <p>Based in the heart of the mountains with deep local knowledge and connections</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <FiGlobe />
              </div>
              <div className="feature-content">
                <h3>International Standards</h3>
                <p>Multilingual guides and services meeting global travel expectations</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <FiShield />
              </div>
              <div className="feature-content">
                <h3>Safety First</h3>
                <p>Comprehensive safety protocols, equipment, and trained emergency response teams</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <FaHeadset />
              </div>
              <div className="feature-content">
                <h3>24/7 Support</h3>
                <p>Round-the-clock assistance before, during, and after your journey</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <FiStar />
              </div>
              <div className="feature-content">
                <h3>Trusted Globally</h3>
                <p>Recommended by travelers from over 30 countries with 95% satisfaction rate</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <FiUsers />
              </div>
              <div className="feature-content">
                <h3>Custom Experiences</h3>
                <p>Tailored itineraries for individuals, groups, and special interest travelers</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="about-section mission-vision">
          <div className="mission-vision-grid">
            <div className="mission-card">
              <div className="card-header">
                <div className="card-icon">
                  <FiTarget />
                </div>
                <h3>Our Mission</h3>
              </div>
              <div className="card-body">
                <p>
                  To inspire and empower global explorers to experience Pakistan's majestic landscapes 
                  through safe, sustainable, and transformative journeys that foster cultural understanding 
                  and environmental stewardship.
                </p>
                <ul className="mission-points">
                  <li><FiCheck /> Promote responsible adventure tourism</li>
                  <li><FiCheck /> Support local communities</li>
                  <li><FiCheck /> Preserve cultural heritage</li>
                  <li><FiCheck /> Deliver exceptional customer experiences</li>
                </ul>
              </div>
            </div>

            <div className="vision-card">
              <div className="card-header">
                <div className="card-icon">
                  <FiEye />
                </div>
                <h3>Our Vision</h3>
              </div>
              <div className="card-body">
                <p>
                  To become the leading adventure travel company in South Asia, recognized globally for 
                  excellence in sustainable tourism, cultural exchange, and life-changing mountain experiences.
                </p>
                <ul className="vision-points">
                  <li><FiCheck /> Expand to 100+ global destinations</li>
                  <li><FiCheck /> Pioneer sustainable tourism practices</li>
                  <li><FiCheck /> Train 500+ local guides by 2030</li>
                  <li><FiCheck /> Achieve carbon-neutral operations</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="about-section team-section">
          <div className="section-header">
            <span className="section-label">Our Team</span>
            <h2>Meet The Experts</h2>
            <p className="section-intro">
              Passionate professionals dedicated to creating your perfect adventure
            </p>
          </div>

          <div className="team-grid">
            <div className="team-member">
              <div className="member-image">
                <img src={MuhammadSiddiq} alt="Muhammad Siddiq" />
                <div className="member-overlay">
                  <div className="social-links">
                    <a href="https://www.facebook.com/gungree" target="_blank" rel="noopener noreferrer">
                      <FaFacebook />
                    </a>
                    <a href="https://www.linkedin.com/in/chogo-ree-siddiq-812658225/" target="_blank" rel="noopener noreferrer">
                      <FaLinkedin />
                    </a>
                    <a href="https://www.instagram.com/chogoree?igsh=N2s2cmQ2eWVocG16" target="_blank" rel="noopener noreferrer">
                      <FaInstagram />
                    </a>
                  </div>
                </div>
              </div>
              <div className="member-info">
                <h3>Muhammad Siddiq</h3>
                <span className="position">Chief Executive Officer (CEO)</span>
                <p className="bio">
                  With over 15 years in adventure tourism, Muhammad leads our operations with expertise 
                  in mountain safety and cultural tourism.
                </p>
              </div>
            </div>

            <div className="team-member">
              <div className="member-image">
                <img src={MaroofAwaisQarni} alt="Maroof Awais Qarni" />
                <div className="member-overlay">
                  <div className="social-links">
                    <a href="https://www.facebook.com/mr.awaisqarnii" target="_blank" rel="noopener noreferrer">
                      <FaFacebook />
                    </a>
                    <a href="https://www.linkedin.com/in/maroofawaisqarni/" target="_blank" rel="noopener noreferrer">
                      <FaLinkedin />
                    </a>
                    <a href="https://www.instagram.com/mr_awaisqarni?igsh=cHp1OW9qeWt4YWlk" target="_blank" rel="noopener noreferrer">
                      <FaInstagram />
                    </a>
                  </div>
                </div>
              </div>
              <div className="member-info">
                <h3>Maroof Awais Qarni</h3>
                <span className="position">Managing Director (MD)</span>
                <p className="bio">
                  Strategic visionary with expertise in international tourism development and 
                  sustainable business practices.
                </p>
              </div>
            </div>

            <div className="team-member">
              <div className="member-image">
                <img src={Francescs} alt="Francesca Lollio" />
                <div className="member-overlay">
                  <div className="social-links">
                    <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer">
                      <FaFacebook />
                    </a>
                    <a href="https://www.linkedin.com/in/francesca-lollio-34965710b/" target="_blank" rel="noopener noreferrer">
                      <FaLinkedin />
                    </a>
                    <a href="https://www.instagram.com/francescs.lollio" target="_blank" rel="noopener noreferrer">
                      <FaInstagram />
                    </a>
                  </div>
                </div>
              </div>
              <div className="member-info">
                <h3>Francesca Lollio</h3>
                <span className="position">Europe Representative</span>
                <p className="bio">
                  Bringing European travel expertise and multilingual capabilities to enhance 
                  our international client experience.
                </p>
              </div>
            </div>

            <div className="team-member">
              <div className="member-image">
                <img src={Joy} alt="Joy Lim" />
                <div className="member-overlay">
                  <div className="social-links">
                    <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer">
                      <FaFacebook />
                    </a>
                    <a href="https://www.linkedin.com/in/" target="_blank" rel="noopener noreferrer">
                      <FaLinkedin />
                    </a>
                    <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer">
                      <FaInstagram />
                    </a>
                  </div>
                </div>
              </div>
              <div className="member-info">
                <h3>Joy Lim</h3>
                <span className="position">Singapore Representative</span>
                <p className="bio">
                  Connects Asian travelers with authentic Pakistani adventures through 
                  cultural understanding and personalized service.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="cta-content">
            <h2>Ready for Your Adventure?</h2>
            <p>
              Join thousands of satisfied travelers who've experienced Pakistan with us. 
              Let's create your perfect journey together.
            </p>
            <div className="cta-buttons">
              <a href="/contact" className="btn-primary">
                Contact Us Now
              </a>
              <a href="/" className="btn-secondary">
                Browse Tours
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;